import { useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactElement } from "react";
import { NavLink } from "react-router";
import type { MyCommunity, RailFolder, RailItem } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { useUI } from "../../lib/uiStore";
import { CommunityIcon, useReducedMotion } from "../../components/ui/Avatar";
import { Badge } from "../../components/ui/Controls";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconAdd } from "../../components/icons";
import { FolderDialog } from "./FolderDialog";
import { FolderSymbol, FolderTile, folderTint } from "./FolderGlyph";
import { CommunityContextMenu, FolderContextMenu } from "./RailMenus";
import { combine, locate, moveCommunity, moveTop, sameLayout, stepCommunity, stepTop, topIndex, updateFolder } from "./layout";
import { measure, rowAt, usePointerDrag } from "./usePointerDrag";
import { DragLayer } from "./DragLayer";
import { currentRail, folderLabel, saveRail, useRail, useRailUI, type RailNode } from "./useRail";

/*
 * The communities part of the rail: drag to reorder, drop a community on
 * another to make a folder, drag in and out of folders, reorder folders.
 * Keyboard: Alt+↑/↓ on a focused community or folder moves it one step.
 * Right-click (long-press on touch) for menus.
 */

type Source = { kind: "community" | "folder"; id: string };
type Target = { type: "gap"; folderId: string | null; index: number; y: number; indent: boolean } | { type: "combine"; id: string };

const HALF_GAP = 4; // half of gap-2

export function RailCommunities({ activeCommunityId }: { activeCommunityId?: string }) {
  const { layout, nodes } = useRail();
  const { open, setOpen, editing, setEditing } = useRailUI();
  const setCreateCommunityOpen = useUI((s) => s.setCreateCommunityOpen);
  const reduced = useReducedMotion();
  const scrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [announce, setAnnounce] = useState("");
  const hintId = useId();

  const nameOf = (s: Source) => {
    const n = nodes.find((x) => x.id === s.id);
    if (n?.kind === "folder") return folderLabel(n.folder.name, n.communities);
    for (const x of nodes) {
      if (x.kind === "community" && x.id === s.id) return x.community.name;
      if (x.kind === "folder") for (const c of x.communities) if (c.id === s.id) return c.name;
    }
    return "Item";
  };

  const hitTest = (_x: number, y: number, src: Source): Target | null => {
    const root = listRef.current;
    const rows = measure(root, "[data-rail-row]").map((r) => ({
      ...r,
      kind: r.el.dataset.railRow as "community" | "folder",
      id: r.el.dataset.id!,
      folder: r.el.dataset.folder || null,
      index: Number(r.el.dataset.index),
      open: r.el.dataset.open === "true",
    }));
    const r = rowAt(rows, y);
    if (!r || !root) return null;
    const rel = (y - r.rect.top) / Math.max(1, r.rect.height);
    const block = (fid: string) => root.querySelector<HTMLElement>(`[data-rail-block="${fid}"]`)?.getBoundingClientRect() ?? r.rect;
    const gap = (folderId: string | null, index: number, lineY: number): Target | null => {
      // Hide no-op drops (right where the item already is).
      if (src.kind === "folder") {
        const at = topIndex(layout, src.id);
        if (folderId === null && (index === at || index === at + 1)) return null;
      } else {
        const loc = locate(layout, src.id);
        if (loc && loc.folderId === folderId && (index === loc.index || index === loc.index + 1)) return null;
      }
      return { type: "gap", folderId, index, y: lineY, indent: folderId !== null };
    };

    if (src.kind === "folder") {
      const fid = r.folder ?? (r.kind === "folder" && r.open ? r.id : null);
      if (fid) {
        const b = block(fid);
        const i = topIndex(layout, fid);
        return y < (b.top + b.bottom) / 2 ? gap(null, i, b.top - HALF_GAP) : gap(null, i + 1, b.bottom + HALF_GAP);
      }
      return rel < 0.5 ? gap(null, r.index, r.rect.top - HALF_GAP) : gap(null, r.index + 1, r.rect.bottom + HALF_GAP);
    }

    if (r.kind === "community" && r.id === src.id) return null;
    if (r.folder) {
      const f = layout.find((it): it is RailFolder => it.kind === "folder" && it.id === r.folder);
      if (f && r.index === f.communityIds.length - 1 && rel > 0.75) return gap(null, topIndex(layout, f.id) + 1, block(f.id).bottom + HALF_GAP);
      return rel < 0.5 ? gap(r.folder, r.index, r.rect.top - HALF_GAP) : gap(r.folder, r.index + 1, r.rect.bottom + HALF_GAP);
    }
    if (r.kind === "folder") {
      if (rel < 0.25) return gap(null, r.index, (r.open ? block(r.id).top : r.rect.top) - HALF_GAP);
      if (rel > 0.75) return r.open ? gap(r.id, 0, r.rect.bottom + HALF_GAP) : gap(null, r.index + 1, r.rect.bottom + HALF_GAP);
      return locate(layout, src.id)?.folderId === r.id ? null : { type: "combine", id: r.id };
    }
    if (rel < 0.25) return gap(null, r.index, r.rect.top - HALF_GAP);
    if (rel > 0.75) return gap(null, r.index + 1, r.rect.bottom + HALF_GAP);
    return { type: "combine", id: r.id };
  };

  const commit = (next: RailItem[], src: Source, how: string) => {
    if (sameLayout(next, layout)) return;
    saveRail(next);
    setAnnounce(`${nameOf(src)} ${how}`);
  };

  const onDrop = (src: Source, t: Target) => {
    if (t.type === "combine") {
      const target = layout.find((it) => it.id === t.id);
      if (target?.kind === "folder") commit(moveCommunity(layout, src.id, { folderId: t.id, index: target.communityIds.length }), src, "moved into the folder");
      else commit(combine(layout, src.id, t.id).items, src, "grouped into a new folder");
    } else if (src.kind === "folder") commit(moveTop(layout, src.id, t.index), src, "moved");
    else commit(moveCommunity(layout, src.id, { folderId: t.folderId, index: t.index }), src, t.folderId ? "moved into the folder" : "moved");
  };

  const { drag, armed, pointerDown } = usePointerDrag<Source, Target>({ hitTest, onDrop, scrollRef });

  const keyMove = (e: KeyboardEvent, src: Source) => {
    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    const dir = e.key === "ArrowUp" ? -1 : 1;
    commit(src.kind === "folder" ? stepTop(layout, src.id, dir) : stepCommunity(layout, src.id, dir), src, "moved");
    requestAnimationFrame(() => listRef.current?.querySelector<HTMLElement>(`[data-rail-row="${src.kind}"][data-id="${src.id}"] [data-rail-focus]`)?.focus());
  };

  // FLIP: glide rows from where they were to where they are after any layout change.
  const prev = useRef(new Map<string, number>());
  const flipKey = JSON.stringify(layout) + Object.keys(open).join();
  useLayoutEffect(() => {
    const root = listRef.current;
    if (!root) return;
    const base = root.getBoundingClientRect().top;
    const now = new Map<string, number>();
    root.querySelectorAll<HTMLElement>("[data-rail-row]").forEach((el) => {
      const key = `${el.dataset.railRow}:${el.dataset.id}`;
      const top = el.getBoundingClientRect().top - base;
      now.set(key, top);
      const was = prev.current.get(key);
      if (!reduced && was !== undefined && Math.abs(was - top) > 1)
        el.animate([{ transform: `translateY(${was - top}px)` }, { transform: "none" }], { duration: 200, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" });
    });
    prev.current = now;
  }, [flipKey, reduced]);

  const dragging = drag?.source ?? null;
  const combineId = drag?.target?.type === "combine" ? drag.target.id : null;
  const railRect = drag ? scrollRef.current?.getBoundingClientRect() : null;
  const editingFolder = editing ? (currentRail().find((it) => it.kind === "folder" && it.id === editing) as RailFolder | undefined) : undefined;
  const editingNode = nodes.find((n) => n.kind === "folder" && n.id === editing) as Extract<RailNode, { kind: "folder" }> | undefined;

  const communityRow = (c: MyCommunity, index: number, folderId: string | null) => (
    <CommunityRow
      key={c.id}
      c={c}
      index={index}
      folderId={folderId}
      active={activeCommunityId === c.id}
      dragging={dragging?.id === c.id}
      lifted={armed?.id === c.id}
      combine={combineId === c.id}
      tooltips={!drag}
      hintId={hintId}
      onPointerDown={(e) => pointerDown(e, { kind: "community", id: c.id })}
      onKeyDown={(e) => keyMove(e, { kind: "community", id: c.id })}
    />
  );

  return (
    <div ref={scrollRef} className="no-scrollbar flex min-h-0 w-full flex-1 select-none flex-col items-center gap-2 overflow-y-auto pb-2">
      <div ref={listRef} role="list" aria-label="Your communities" className="flex w-full flex-col items-center gap-2 empty:hidden">
        {nodes.map((n, i) =>
          n.kind === "community" ? (
            communityRow(n.community, i, null)
          ) : (
            <FolderRow
              key={n.id}
              node={n}
              index={i}
              open={!!open[n.id]}
              onToggle={(o) => setOpen(n.id, o)}
              activeCommunityId={activeCommunityId}
              dragging={dragging?.id === n.id}
              lifted={armed?.id === n.id}
              combine={combineId === n.id}
              tooltips={!drag}
              hintId={hintId}
              onPointerDown={(e) => pointerDown(e, { kind: "folder", id: n.id })}
              onKeyDown={(e) => keyMove(e, { kind: "folder", id: n.id })}
              renderChild={communityRow}
            />
          ),
        )}
      </div>
      <Tooltip label="Create a community" side="right">
        <button
          onClick={() => setCreateCommunityOpen(true)}
          aria-label="Create a community"
          className="flex size-10 shrink-0 items-center justify-center rounded-[11px] border border-dashed border-line-strong text-fg-3 transition-colors hover:border-fg-3 hover:text-fg"
        >
          <IconAdd className="icon" />
        </button>
      </Tooltip>

      <span id={hintId} className="sr-only">
        Press Alt and the arrow keys to move. Right-click for more options.
      </span>
      <span role="status" aria-live="polite" className="sr-only">
        {announce}
      </span>

      {drag && (
        <DragLayer>
          {drag.target?.type === "gap" && railRect && (
            <span
              aria-hidden
              className="pointer-events-none fixed z-[70] h-0.5 rounded-full bg-accent"
              style={{ top: drag.target.y - 1, left: railRect.left + (drag.target.indent ? 16 : 10), width: railRect.width - (drag.target.indent ? 32 : 20) }}
            />
          )}
          <DragGhost source={drag.source} x={drag.x} y={drag.y} nodes={nodes} />
        </DragLayer>
      )}

      <FolderDialog
        folder={editingFolder ?? null}
        communities={editingNode?.communities ?? []}
        open={!!editingFolder}
        onOpenChange={(o) => !o && setEditing(null)}
        onSave={(d) => editing && saveRail(updateFolder(currentRail(), editing, d), { error: "Couldn't save the folder" })}
      />
    </div>
  );
}

function Pill({ state }: { state: "active" | "unread" | "none" }) {
  return (
    <span
      aria-hidden
      className={cn(
        "absolute left-0 top-1/2 w-1 -translate-y-1/2 rounded-r-full bg-fg transition-all duration-200",
        state === "active" ? "h-8" : state === "unread" ? "h-2 group-hover:h-4" : "h-0 group-hover:h-4",
      )}
    />
  );
}

interface RowProps {
  index: number;
  dragging: boolean;
  lifted: boolean;
  combine: boolean;
  tooltips: boolean;
  hintId: string;
  onPointerDown: (e: PointerEvent) => void;
  onKeyDown: (e: KeyboardEvent) => void;
}

const tileState = (p: Pick<RowProps, "dragging" | "lifted" | "combine">) =>
  cn(
    "[-webkit-touch-callout:none] transition-[opacity,transform,box-shadow] duration-150",
    p.dragging && "opacity-40",
    (p.lifted || p.combine) && "scale-110",
    p.combine && "ring-2 ring-accent ring-offset-2 ring-offset-sidebar",
  );

function CommunityRow({ c, folderId, active, ...p }: RowProps & { c: MyCommunity; folderId: string | null; active: boolean }) {
  return (
    <CommunityContextMenu community={c}>
      <div role="listitem" data-rail-row="community" data-id={c.id} data-folder={folderId ?? ""} data-index={p.index} className="group relative flex w-full justify-center">
        <Pill state={active ? "active" : c.unread ? "unread" : "none"} />
        <Tooltip label={c.name} side="right" disabled={!p.tooltips}>
          <NavLink
            to={`/c/${c.id}`}
            data-rail-focus
            aria-label={`${c.name}${c.mentionCount ? `, ${c.mentionCount} mentions` : c.unread ? ", unread messages" : ""}`}
            aria-describedby={p.hintId}
            onPointerDown={p.onPointerDown}
            onKeyDown={p.onKeyDown}
            className={cn("relative rounded-[11px]", !active && !p.dragging && "opacity-85 hover:opacity-100", tileState(p))}
          >
            <CommunityIcon src={c.iconUrl} name={c.name} size="md" />
            {c.mentionCount > 0 && <Badge count={c.mentionCount} className="absolute -bottom-1 -right-1 ring-[3px] ring-sidebar" />}
          </NavLink>
        </Tooltip>
      </div>
    </CommunityContextMenu>
  );
}

function FolderRow({
  node,
  open,
  onToggle,
  activeCommunityId,
  renderChild,
  ...p
}: RowProps & {
  node: Extract<RailNode, { kind: "folder" }>;
  open: boolean;
  onToggle: (open: boolean) => void;
  activeCommunityId?: string;
  renderChild: (c: MyCommunity, index: number, folderId: string) => ReactElement;
}) {
  const { folder, communities } = node;
  const label = folderLabel(folder.name, communities);
  const mentions = communities.reduce((n, c) => n + c.mentionCount, 0);
  const unread = communities.some((c) => c.unread);
  const containsActive = communities.some((c) => c.id === activeCommunityId);
  const tint = folderTint(folder.color);
  const status = mentions ? `, ${mentions} mentions` : unread ? ", unread messages" : "";

  const header = (
    <FolderContextMenu folder={folder} communities={communities}>
      <div role="listitem" data-rail-row="folder" data-id={folder.id} data-index={p.index} data-open={open} className="group relative flex w-full justify-center">
        {!open && <Pill state={containsActive ? "active" : unread ? "unread" : "none"} />}
        <Tooltip label={label} side="right" disabled={!p.tooltips}>
          <button
            type="button"
            data-rail-focus
            aria-expanded={open}
            aria-label={`${label}, folder${open ? "" : status}`}
            aria-describedby={p.hintId}
            onClick={() => onToggle(!open)}
            onPointerDown={p.onPointerDown}
            onKeyDown={p.onKeyDown}
            className={cn("relative rounded-[11px]", tileState(p))}
          >
            {open ? (
              <span className="flex size-10 items-center justify-center">
                <FolderSymbol icon={folder.icon ?? "folder"} className={folder.icon && !/^[a-z]+$/.test(folder.icon) ? "text-xl" : "size-[22px]"} style={tint.fg} />
              </span>
            ) : (
              <FolderTile folder={folder} communities={communities} />
            )}
            {!open && mentions > 0 && <Badge count={mentions} className="absolute -bottom-1 -right-1 ring-[3px] ring-sidebar" />}
          </button>
        </Tooltip>
      </div>
    </FolderContextMenu>
  );

  if (!open) return header;
  return (
    <div data-rail-block={folder.id} className={cn("relative flex w-full flex-col items-center gap-2 pb-1.5 pt-1", p.dragging && "opacity-40")}>
      <span aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 w-12 -translate-x-1/2 rounded-[14px]" style={tint.tile} />
      {header}
      <div role="list" aria-label={label} className="relative flex w-full flex-col items-center gap-2">
        {communities.map((c, j) => renderChild(c, j, folder.id))}
      </div>
    </div>
  );
}

function DragGhost({ source, x, y, nodes }: { source: Source; x: number; y: number; nodes: RailNode[] }) {
  let body: ReactElement | null = null;
  for (const n of nodes) {
    if (n.kind === "folder" && n.id === source.id) body = <FolderTile folder={n.folder} communities={n.communities} />;
    const c = n.kind === "community" ? (n.id === source.id ? n.community : null) : n.communities.find((x) => x.id === source.id);
    if (c && source.kind === "community") body = <CommunityIcon src={c.iconUrl} name={c.name} size="md" />;
  }
  if (!body) return null;
  return (
    <div aria-hidden className="pointer-events-none fixed left-0 top-0 z-[80] rounded-[11px] opacity-90 shadow-pop" style={{ transform: `translate(${x - 20}px, ${y - 20}px) scale(1.05)` }}>
      {body}
    </div>
  );
}
